import React from 'react'
import { useState, useEffect } from 'react'
import './LandingPage.css'
import { Link } from 'react-router-dom'

function LandingPage() {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isNavOpen, setIsNavOpen] = useState(false)
  const [openFaqIndex, setOpenFaqIndex] = useState(null)
  const [isAnnual, setIsAnnual] = useState(false)
  
  useEffect(() => {
    // Add animation when component mounts
    setIsLoaded(true)
    
    // Initialize floating elements with random positions
    const floatingElements = document.querySelectorAll('.floating-element');
    floatingElements.forEach(element => {
      const randomX = Math.random() * 100;
      const randomY = Math.random() * 100;
      element.style.left = `${randomX}%`;
      element.style.top = `${randomY}%`;
    });
  }, [])

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen)
  }

  // Close mobile nav when clicking on a link
  const handleNavLinkClick = () => {
    if (window.innerWidth <= 992) {
      setIsNavOpen(false)
    }
  }
  
  // Toggle FAQ open/close
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  return (
    <div className="landing-container">
      <nav className="navbar">
        <div className="nav-logo">
          <span className="logo-text">COSMIC<span>BUILDER</span></span>
        </div>
        <div className="nav-toggle" onClick={toggleNav}>
          <div className={`hamburger ${isNavOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <ul className={`nav-links ${isNavOpen ? 'active' : ''}`}>
          <li><a href="#features" onClick={handleNavLinkClick}>Features</a></li>
          <li><a href="#pricing" onClick={handleNavLinkClick}>Pricing</a></li>
          <li><a href="#templates" onClick={handleNavLinkClick}>Templates</a></li>
          <li><a href="#contact" onClick={handleNavLinkClick}>Contact</a></li>
          <li className="nav-auth signin">
            <Link to="/signin" onClick={handleNavLinkClick}>Sign In</Link>
          </li>
          <li className="nav-auth signup">
            <Link to="/signup" onClick={handleNavLinkClick}>Sign Up</Link>
          </li>
        </ul>
      </nav>
      
      <div className={`hero-section ${isLoaded ? 'loaded' : ''}`}>
        <div className="nebula-bg"></div>
        
        {/* Add floating elements for depth and interest */}
        <div className="floating-elements">
          <div className="floating-element planet-1"></div>
          <div className="floating-element planet-2"></div>
          <div className="floating-element spacecraft"></div>
          <div className="floating-element star-cluster-1"></div>
          <div className="floating-element star-cluster-2"></div>
        </div>
        
        {/* Add animated glowing grid */}
        <div className="grid-overlay"></div>
        
        {/* Add particle effect */}
        <div className="particles">
          {[...Array(50)].map((_, index) => (
            <div key={index} className="particle"></div>
          ))}
        </div>
        
        {/* Add orbit rings */}
        <div className="orbital-ring ring-1"></div>
        <div className="orbital-ring ring-2"></div>
        
        <div className="content">
          {/* Add a decorative element above the title */}
          <div className="cosmic-insignia">
            <div className="insignia-inner"></div>
          </div>
          
          <h1 className="glitch-text">COSMIC<span>BUILDER</span></h1>
          <p className="slogan">Create Websites That Are Out Of This World</p>
          
          {/* Add a futuristic mockup element */}
          <div className="cosmic-mockup">
            <div className="mockup-screen">
              <div className="mockup-content">
                <div className="mockup-lines"></div>
                <div className="mockup-circles"></div>
              </div>
            </div>
            <div className="mockup-base"></div>
          </div>
          
          <div className="cta-buttons">
            <button className="primary-btn">
              <span className="btn-glow"></span>
              Get Started
            </button>
            <button className="secondary-btn">
              <span className="btn-glow"></span>
              View Templates
            </button>
          </div>
          
          {/* Add floating code snippets */}
          <div className="floating-code code-left">
            <div className="code-line"></div>
            <div className="code-line"></div>
            <div className="code-line"></div>
          </div>
          <div className="floating-code code-right">
            <div className="code-line"></div>
            <div className="code-line"></div>
            <div className="code-line"></div>
          </div>
        </div>
      </div>
      
      <div className="features-section">
        <h2>Stellar Features</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* New Pricing Section */}
      <div className="pricing-section" id="pricing">
        <div className="pricing-stars">
          <div className="shooting-star"></div>
          <div className="shooting-star"></div>
          <div className="shooting-star"></div>
        </div>
        
        <h2>Choose Your Universe</h2>
        <p className="pricing-subtitle">Select the perfect plan for your cosmic journey</p>
        
        <div className="pricing-toggle">
          <span className={`toggle-option ${!isAnnual ? 'active' : ''}`}>Monthly</span>
          <label className="switch">
            <input 
              type="checkbox" 
              checked={isAnnual} 
              onChange={() => setIsAnnual(!isAnnual)}
            />
            <span className="slider"></span>
          </label>
          <span className={`toggle-option ${isAnnual ? 'active' : ''}`}>
            Annual
            <span className="save-badge">Save 20%</span>
          </span>
        </div>
        
        <div className="pricing-grid">
          {pricingPlans.map((plan, index) => (
            <div 
              key={index} 
              className={`pricing-card ${plan.popular ? 'popular' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              
              <div className="pricing-card-header">
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  <span className="currency">$</span>
                  <span className="amount">{isAnnual ? plan.annualPrice : plan.price}</span>
                  <span className="period">/{isAnnual ? 'year' : 'month'}</span>
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>
              
              <div className="pricing-card-features">
                <ul>
                  {plan.features.map((feature, i) => (
                    <li key={i} className={feature.included ? '' : 'not-included'}>
                      <span className="feature-icon">
                        {feature.included ? '✓' : '×'}
                      </span>
                      {feature.text}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="pricing-card-cta">
                <button className={`pricing-btn ${plan.popular ? 'primary-btn' : 'outline-btn'}`}>
                  <span className="btn-glow"></span>
                  {plan.ctaText}
                </button>
              </div>
              
              {plan.popular && <div className="card-glow"></div>}
            </div>
          ))}
        </div>
        
        <div className="enterprise-box">
          <div className="enterprise-content">
            <div className="enterprise-text">
              <h3>Need a Custom Solution?</h3>
              <p>Contact our sales team for enterprise-level custom pricing and features tailored to your organization's specific needs.</p>
            </div>
            <a href="#contact" className="enterprise-cta">Contact Sales</a>
          </div>
          <div className="enterprise-bg"></div>
        </div>
      </div>
      
      {/* New Trusted By Section */}
      <div className="testimonials-section">
        <h2>Trusted By Designers</h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card">
              <p className="testimonial-text">{testimonial.text}</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <img src={testimonial.avatar} alt={testimonial.name} />
                </div>
                <div className="author-info">
                  <h4>{testimonial.name}</h4>
                  <p className="author-username">{testimonial.username}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* New FAQ Section */}
      <div className="faq-section" id="faq">
        <div className="faq-background">
          <div className="star star1"></div>
          <div className="star star2"></div>
          <div className="star star3"></div>
          <div className="planet-small"></div>
        </div>
        
        <h2>Frequently Asked Questions</h2>
        
        <div className="faq-container">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <div className="faq-icon">
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="faq-cta">
          <p>Still have questions?</p>
          <a href="#contact" className="contact-btn">Contact Us</a>
        </div>
      </div>
      
      {/* New Footer Section */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-grid">
            <div className="footer-column brand-column">
              <div className="footer-logo">
                <span className="logo-text">COSMIC<span>BUILDER</span></span>
              </div>
              <p className="footer-tagline">Create websites that are out of this world</p>
              <div className="social-links">
                <a href="#" className="social-link"><i className="fab fa-twitter"></i></a>
                <a href="#" className="social-link"><i className="fab fa-github"></i></a>
                <a href="#" className="social-link"><i className="fab fa-linkedin"></i></a>
                <a href="#" className="social-link"><i className="fab fa-discord"></i></a>
              </div>
            </div>
            
            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#templates">Templates</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4>Resources</h4>
              <ul className="footer-links">
                <li><a href="#">Documentation</a></li>
                <li><a href="#">Tutorials</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Community</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="#">About Us</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="#">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p className="copyright">© {new Date().getFullYear()} CosmicBuilder. All rights reserved.</p>
            <div className="legal-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </div>
          </div>
        </div>
        
        <div className="footer-glow"></div>
      </footer>
    </div>
  )
}

// Sample features data
const features = [
  {
    icon: '🚀',
    title: 'Lightning Fast',
    description: 'Build responsive websites in minutes with our intuitive drag-and-drop interface'
  },
  {
    icon: '🌌',
    title: 'Infinite Possibilities',
    description: 'Hundreds of customizable templates and components at your fingertips'
  },
  {
    icon: '🔧',
    title: 'Advanced Controls',
    description: 'Fine-tune every aspect of your design with powerful developer tools'
  },
  {
    icon: '🛰️',
    title: 'Cloud Hosting',
    description: 'One-click deployment to our reliable and secure hosting platform'
  }
]

// Sample testimonials data
const testimonials = [
  {
    text: "It practically solves every issue I have with Webflow and Framer. This is so much easier to use as a developer!",
    name: "Jaiden Lee",
    username: "@buildjaiden",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    text: "Amazing.",
    name: "shadcn",
    username: "@shadcn",
    avatar: "https://randomuser.me/api/portraits/men/44.jpg"
  },
  {
    text: "I really have been missing this product for years. It feels like a much more natural way of working.",
    name: "Ninjanordbo",
    username: "@ninjanordbo",
    avatar: "https://randomuser.me/api/portraits/women/28.jpg"
  },
  {
    text: "It's like Framer and v0 had a baby!",
    name: "Ray",
    username: "@RayFernando1337",
    avatar: "https://randomuser.me/api/portraits/men/62.jpg"
  },
  {
    text: "This is awesome. Better than Framer/Wordpress as you can take the code and continue working on it instantly yourself.",
    name: "soumya dot dev",
    username: "@geekysrm",
    avatar: "https://randomuser.me/api/portraits/men/47.jpg"
  },
  {
    text: "This is so tastefully built. Loving it.",
    name: "Kabir Asani",
    username: "@KabirAsani",
    avatar: "https://randomuser.me/api/portraits/men/19.jpg"
  }
]

// Sample FAQs data
const faqs = [
  {
    question: "What makes CosmicBuilder different from other website builders?",
    answer: "CosmicBuilder combines the ease of a visual drag-and-drop interface with powerful developer tools. You can quickly build a site visually, then dig into the code for complete customization. Plus, our sci-fi inspired templates are truly unique in the market."
  },
  {
    question: "Do I need coding experience to use CosmicBuilder?",
    answer: "Not at all! CosmicBuilder is designed for both beginners and professionals. You can create stunning websites without writing a single line of code using our visual editor, or dive into the code if you want more control."
  },
  {
    question: "Can I migrate my existing website to CosmicBuilder?",
    answer: "Yes! We provide tools to easily import your content from WordPress, Wix, Squarespace, and other popular platforms. Our migration assistant will help transfer your pages, posts, and media with just a few clicks."
  },
  {
    question: "How much does CosmicBuilder cost?",
    answer: "CosmicBuilder offers flexible pricing plans starting at $12/month for the Basic plan. We also have Pro ($29/month) and Enterprise (custom pricing) plans for more advanced features. All plans come with a 14-day free trial, no credit card required."
  },
  {
    question: "Is there a limit to how many websites I can create?",
    answer: "The Basic plan allows you to create up to 3 websites, while the Pro plan supports up to 10 sites. Our Enterprise plan offers unlimited website creation to suit larger organizations and agencies."
  },
  {
    question: "Do you offer custom domain names?",
    answer: "Yes! You can either purchase a domain directly through us or connect your existing domain from another provider. All paid plans include at least one free custom domain for the first year."
  }
]

// Pricing plans data
const pricingPlans = [
  {
    name: "Nebula",
    price: 12,
    annualPrice: 115,
    description: "Perfect for individuals and small projects",
    features: [
      { text: "3 websites", included: true },
      { text: "Basic templates", included: true },
      { text: "1GB storage", included: true },
      { text: "Community support", included: true },
      { text: "Custom domain", included: true },
      { text: "Advanced analytics", included: false },
      { text: "Priority support", included: false },
    ],
    ctaText: "Start Free Trial",
    popular: false
  },
  {
    name: "Supernova",
    price: 29,
    annualPrice: 279,
    description: "Ideal for professionals and growing businesses",
    features: [
      { text: "10 websites", included: true },
      { text: "Premium templates", included: true },
      { text: "10GB storage", included: true },
      { text: "Community support", included: true },
      { text: "Custom domain", included: true },
      { text: "Advanced analytics", included: true },
      { text: "Priority support", included: true },
    ],
    ctaText: "Start Free Trial",
    popular: true
  },
  {
    name: "Galaxy",
    price: 49,
    annualPrice: 469,
    description: "Advanced features for teams and businesses",
    features: [
      { text: "Unlimited websites", included: true },
      { text: "All templates", included: true },
      { text: "100GB storage", included: true },
      { text: "Community support", included: true },
      { text: "Custom domain", included: true },
      { text: "Advanced analytics", included: true },
      { text: "24/7 priority support", included: true },
    ],
    ctaText: "Start Free Trial",
    popular: false
  }
]

export default LandingPage